import { useState } from "react";
import { Hoja } from "@/components/Hoja";
import { RemansoFigure } from "@/components/Brand";
import { isIOS } from "@/lib/push";
import { isAndroid, isIOSSafari, isMacSafari, promptInstall, usePwa } from "@/lib/pwa";

const Steps = ({ items, testId }) => (
  <ol className="mt-3 space-y-3" data-testid={testId}>
    {items.map((t, i) => (
      <li key={t} className="flex gap-3 text-[15px] text-tinta leading-relaxed">
        <span className="font-hand text-[24px] leading-[1.1] text-acento w-5 shrink-0">{i + 1}</span>
        <span>{t}</span>
      </li>
    ))}
  </ol>
);

const InstallButton = () => {
  const { canPrompt } = usePwa();
  if (!canPrompt) return null;
  return <button type="button" className="btn-primary mt-4" onClick={promptInstall} data-testid="install-native-button">Instalar Pensive</button>;
};

const CopyLink = () => {
  const [done, setDone] = useState(false);
  const copy = () => navigator.clipboard?.writeText(window.location.origin).then(() => setDone(true)).catch(() => {});
  return (
    <div className="rounded-[16px] bg-superficie px-4 py-3 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="ios-open-in-safari">
      <p className="text-[14px] text-tinta">Para instalarla, abre Pensive en Safari.</p>
      <button type="button" className="btn-secondary px-4 py-2 text-[14px]" onClick={copy} data-testid="copy-link-button">{done ? "Enlace copiado" : "Copiar enlace"}</button>
    </div>
  );
};

const Android = () => {
  const { canPrompt } = usePwa();
  return canPrompt ? <InstallButton /> : (
    <Steps testId="steps-android" items={[
      "Toca el menú ⋮ arriba a la derecha.",
      "Elige \u201cInstalar app\u201d o \u201cAgregar a la pantalla principal\u201d.",
      "Confirma con \u201cInstalar\u201d.",
    ]} />
  );
};

const Ios = ({ primary }) => (
  <>
    {primary && !isIOSSafari() && <CopyLink />}
    <Steps testId="steps-ios" items={[
      "Toca el botón Compartir (el cuadro con la flecha hacia arriba). En algunas versiones está dentro del menú ···.",
      "Desliza y elige \u201cAgregar a pantalla de inicio\u201d.",
      "Toca \u201cAgregar\u201d. Pensive aparece junto a tus apps.",
    ]} />
  </>
);

const Desktop = () => (
  <>
    <p className="mt-3 text-[15px] text-tinta leading-relaxed" data-testid="steps-desktop">Haz clic en el ícono de instalar al final de la barra de direcciones, o en el menú ⋮ → Instalar Pensive.</p>
    <InstallButton />
  </>
);

const Mac = () => <p className="mt-3 text-[15px] text-tinta leading-relaxed" data-testid="steps-mac">En el menú Archivo, elige Agregar al Dock.</p>;

const DEVICES = [
  { id: "android", title: "Android (Chrome, Edge o Samsung Internet)", Body: Android },
  { id: "ios", title: "iPhone y iPad (Safari)", Body: Ios },
  { id: "desktop", title: "Computadora (Chrome o Edge)", Body: Desktop },
  { id: "mac", title: "Mac (Safari)", Body: Mac },
];

const detect = () => (isIOS() ? "ios" : isAndroid() ? "android" : isMacSafari() ? "mac" : "desktop");

export default function Instalar() {
  const { installed } = usePwa();
  if (installed) {
    return (
      <Hoja testId="screen-instalar" title="Lleva Pensive en tu pantalla de inicio">
        <div className="flex items-center gap-5" data-testid="install-already">
          <RemansoFigure estado="escucha" height={96} testId="install-remanso" />
          <p className="text-[17px] text-tinta">Ya tienes Pensive instalada.</p>
        </div>
      </Hoja>
    );
  }
  const mine = detect();
  const primary = DEVICES.find((d) => d.id === mine);
  return (
    <Hoja testId="screen-instalar" title="Lleva Pensive en tu pantalla de inicio">
      <div className="max-w-2xl">
        <p className="text-[15px] text-sec leading-relaxed -mt-4">Se abre como una app, sin barra del navegador, y en iPhone te permite recibir recordatorios.</p>
        <section className="mt-8" data-testid={`install-primary-${mine}`}>
          <h2 className="font-serif text-[24px] text-tinta">{primary.title}</h2>
          <primary.Body primary />
        </section>
        <section className="mt-10 pt-6 border-t border-linea" data-testid="install-others">
          <h2 className="eyebrow">Otros dispositivos</h2>
          <div className="mt-3 divide-y divide-linea">
            {DEVICES.filter((d) => d.id !== mine).map(({ id, title, Body }) => (
              <details key={id} className="group py-3" data-testid={`install-other-${id}`}>
                <summary className="cursor-pointer list-none rounded-[8px] text-[16px] text-tinta flex items-center justify-between gap-4">
                  {title}<span className="text-meta transition-transform group-open:rotate-45 text-[20px] leading-none" aria-hidden="true">+</span>
                </summary>
                <Body />
              </details>
            ))}
          </div>
        </section>
        <p className="mt-10 text-[13px] text-meta leading-relaxed" data-testid="install-shared-device-note">Si compartes este dispositivo con alguien, cierra sesión al terminar de escribir. La app instalada mantiene tu sesión abierta.</p>
      </div>
    </Hoja>
  );
}
